// Shared visual tokens + style helpers, ported verbatim from the design
// (Incident Lens.dc.html). Grayscale-first: highlight/claim kinds differ by glyph
// + line-style, not hue (accessibility §7); device=red ◆, axis=green ▲ accents only.

import type { CSSProperties } from "react";
import type { ClaimStance, HighlightKind } from "./types";

export const INK = "#37352f";
export const INK_DEEP = "#26241f";

export const SURFACE = {
  warm: "#f7f6f3",
  warmer: "#fbfbfa",
  chip: "#f1f0ec",
  chipDeep: "#e7e6e1",
} as const;

export interface KindColor {
  fg: string; bg: string; bgActive: string; line: string; dash: string;
}

export function kindColor(kind: HighlightKind | string): KindColor {
  if (kind === "verbatim")
    return { fg: "rgba(55,53,47,.55)", bg: "rgba(55,53,47,.05)", bgActive: "rgba(55,53,47,.1)", line: "rgba(55,53,47,.4)", dash: "dotted" };
  return kind === "device"
    ? { fg: "#b3261e", bg: "rgba(179,38,30,.10)", bgActive: "rgba(179,38,30,.20)", line: "#b3261e", dash: "solid" }
    : { fg: "#1f7a44", bg: "rgba(31,122,68,.10)", bgActive: "rgba(31,122,68,.20)", line: "#1f7a44", dash: "dashed" };
}

export type HighlightStyle = "fill" | "underline" | "bracket";

export function segStyle(kind: HighlightKind | string, hs: HighlightStyle, active: boolean): CSSProperties {
  const c = kindColor(kind);
  const base: CSSProperties = {
    font: "inherit", color: "inherit", cursor: "pointer", padding: "0 1px", margin: 0,
    borderRadius: "2px", position: "relative", transition: "background .12s, box-shadow .12s",
    appearance: "none", WebkitAppearance: "none", background: "transparent", border: "none", lineHeight: "inherit",
  };
  if (hs === "fill") {
    base.background = active ? c.bgActive : c.bg;
    base.borderBottom = "2px " + c.dash + " " + c.line;
    if (active) base.boxShadow = "0 0 0 2px " + c.bgActive;
  } else if (hs === "underline") {
    base.background = active ? c.bg : "transparent";
    base.borderBottom = "2px " + c.dash + " " + c.line;
  } else {
    base.background = active ? c.bg : "transparent";
    base.boxShadow = "inset 0 0 0 1px " + c.line;
    base.borderRadius = "3px";
    base.padding = "0 3px";
  }
  return base;
}

export function flashStyle(kind: HighlightKind | string): CSSProperties {
  const c = kindColor(kind);
  return {
    font: "inherit", color: "inherit", cursor: "pointer", padding: "0 2px", margin: 0, borderRadius: "3px",
    border: "none", appearance: "none", WebkitAppearance: "none", lineHeight: "inherit",
    background: c.bgActive, boxShadow: "0 0 0 2px " + c.bgActive, animation: "flashpulse 1.8s ease-out",
  };
}

// diff insert/delete spans (P5 + P7)
export function insStyle(active: boolean): CSSProperties {
  return { font: "inherit", color: "#176c3a", fontWeight: 600, background: active ? "rgba(31,122,68,.22)" : "rgba(31,122,68,.12)", borderBottom: "2px solid #1f7a44", borderRadius: "2px", padding: "0 1px", cursor: "pointer", border: "none", appearance: "none" };
}
export function delStyle(active: boolean): CSSProperties {
  return { font: "inherit", color: "#a4322b", background: active ? "rgba(179,38,30,.16)" : "transparent", textDecoration: "line-through", textDecorationColor: "rgba(179,38,30,.55)", borderRadius: "2px", padding: "0 1px", cursor: "pointer", border: "none", appearance: "none" };
}

// attribution badge (P4 + P5 grounded claims)
export function badgeFor(st: ClaimStance): { label: string; style: CSSProperties } {
  if (st === "asserted_fact")
    return { label: "Stated as fact", style: { fontSize: "10px", fontWeight: 600, padding: "2px 7px", borderRadius: "4px", background: INK, color: "#fff", letterSpacing: ".01em" } };
  if (st === "attributed")
    return { label: "Attributed", style: { fontSize: "10px", fontWeight: 600, padding: "2px 7px", borderRadius: "4px", background: "#fff", color: "rgba(55,53,47,.6)", border: "1px solid rgba(55,53,47,.2)", letterSpacing: ".01em" } };
  return { label: "Contested", style: { fontSize: "10px", fontWeight: 600, padding: "2px 7px", borderRadius: "4px", background: SURFACE.chip, color: INK, border: "1px dashed rgba(55,53,47,.5)", letterSpacing: ".01em" } };
}

// claim frame-polarity glyph (grayscale: threat=solid, human=open, neutral=light dot)
export const POL_LABEL: Record<string, string> = { threat: "Threat frame", human: "Human frame", neutral: "Neutral" };
export const POL_STYLE: Record<string, CSSProperties> = {
  threat: { flex: "none", marginTop: "5px", width: "9px", height: "9px", borderRadius: "2px", background: INK },
  human: { flex: "none", marginTop: "5px", width: "9px", height: "9px", borderRadius: "2px", background: "transparent", border: "1.5px solid " + INK },
  neutral: { flex: "none", marginTop: "7px", width: "7px", height: "2px", borderRadius: "1px", background: "rgba(55,53,47,.35)" },
};

/** position of value v on a [lo,hi] scale, as a 0..100 percentage. */
export function pos(v: number, scale: [number, number]): number {
  return ((v - scale[0]) / (scale[1] - scale[0])) * 100;
}

// section-label (small caps) — used across panels
export const sectionLabel: CSSProperties = {
  fontSize: "10px", fontWeight: 600, letterSpacing: ".06em", textTransform: "uppercase",
  color: "rgba(55,53,47,.4)",
};
